import { apiClient } from './apiClient';
import type { ApiResponse, PaginatedResponse } from '@/types/api.types';

export type LabResultSource = 'quest' | 'vibrant' | 'access';
export type LabSearchType = 'any' | 'name' | 'patientId' | 'tags' | 'phone' | 'birthDate' | 'email';

export interface LabResultListItem {
  rowKey: string;
  labResultId: number;
  source: LabResultSource;
  patientId: number;
  patientDisplayId: string | null;
  patientName: string;
  birthDate: string | null;
  orderedAt: string | null;
  orderNumber: string | null;
  orderDisplay: string;
  testsSummary: string | null;
  orderFound: boolean;
  /** "Access Labs" on an Access row; not sent for Quest or Vibrant. */
  sourceName?: string;
  /** The status shown on the row: "Preliminary" / "Final" / "Corrected" on Access. */
  orcStatus?: string | null;
  /** Access only: 'preliminary' | 'final' | 'corrected', or null. */
  resultStatus?: AccessResultStatus | null;
}

export interface LabResultObservation {
  code: string;
  label: string;
  value: string;
  unit: string | null;
  referenceRange: string | null;
  flag: string | null;
  abnormal: boolean;
}

export interface LabResultSection {
  id: string;
  title: string;
  subtitle: string | null;
  collectedAt?: string | null;
  reportedAt?: string | null;
  observations: LabResultObservation[];
}

export interface LabResultFile {
  type: string;
  name: string;
  receivedAt: string | null;
  url: string | null;
  label: string;
  testCode?: string | null;
  aoeUrl?: string | null;
}

export interface LabResultItem {
  labOrderItemId: number;
  testCode: string;
  testName: string;
  resultValue: string | null;
  resultUnit: string | null;
  referenceRange: string | null;
  abnormal: boolean;
  resultReceivedAt: string | null;
}

export interface LabResultSummary {
  controlId?: string | null;
  requestId?: string | null;
  rawResultUrl?: string | null;
  pdfReportUrl?: string | null;
  aoeUrls?: Array<{ label: string; url: string }>;
  canResubmit?: boolean;
  aoeLoaded?: boolean;
  resubmitUrl?: string | null;
  providerName?: string | null;
  providerNpi?: string | null;
}

export interface LabResultDetail {
  id: number;
  source: LabResultSource;
  patientId: number;
  patientDisplayId: string | null;
  patientName: string;
  birthDate: string | null;
  orderedAt: string | null;
  orderNumber: string | null;
  testSummary: string | null;
  orderFound: boolean;
  status: string | null;
  files: LabResultFile[];
  items: LabResultItem[];
  sections: LabResultSection[];
  summary: LabResultSummary;
}

export interface LabMatchCandidate {
  patientId: number;
  displayId: number | string | null;
  firstName: string;
  lastName: string;
  email: string | null;
  cellPhone: string | null;
  homePhone: string | null;
  birthDate: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  matchScore: number;
  matches: {
    firstName: boolean;
    lastName: boolean;
    name: boolean;
    email: boolean;
    phone: boolean;
    dob: boolean;
  };
}

export interface LabOrderFilters {
  search?: string;
  searchType?: LabSearchType;
  provider?: LabResultSource;
  /** Restrict to results with / without a matched patient record. */
  patientMatch?: 'unmatched' | 'matched';
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  perPage?: number;
  export?: boolean;
  sortBy?: 'orderedAt' | 'orderNumber' | 'patientDisplayId' | 'patientName' | 'birthDate' | 'testsSummary';
  sortDir?: 'asc' | 'desc';
}

export interface LabDownloadResult {
  blob: Blob;
  filename: string | null;
  contentType: string | null;
}

export function listLabOrders(
  filters: LabOrderFilters = {},
): Promise<PaginatedResponse<LabResultListItem>> {
  const params: Record<string, string | number | boolean | undefined> = {
    search: filters.search,
    searchType: filters.searchType,
    provider: filters.provider,
    patientMatch: filters.patientMatch,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    page: filters.page,
    perPage: filters.perPage,
    export: filters.export,
    sortBy: filters.sortBy,
    sortDir: filters.sortDir,
  };

  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== ''),
  );

  return apiClient
    .get<PaginatedResponse<LabResultListItem>>('/lab-orders', { params: cleanParams })
    .then((response) => response.data);
}

export function getLabResultDetail(
  source: LabResultSource,
  id: number,
): Promise<LabResultDetail> {
  return apiClient
    .get<ApiResponse<LabResultDetail>>(`/lab-orders/detail/${source}/${id}`)
    .then((response) => response.data.data);
}

// ─── Access Labs result detail (LabOrdersController::accessDetail) ───────────

export type AccessResultStatus = 'preliminary' | 'final' | 'corrected';

/** One version of an Access specimen's result, oldest first. */
export interface AccessResultVersion {
  id: number;
  status: AccessResultStatus | null;
  statusLabel: string | null;
  receivedAt: string | null;
  messageAt: string | null;
  isCurrent: boolean;
}

export interface AccessResultObservation extends LabResultObservation {
  status: string | null;
  notes: string[];
}

export interface AccessResultSection extends Omit<LabResultSection, 'observations'> {
  observations: AccessResultObservation[];
}

export interface AccessLabResultDetail {
  id: number;
  source: 'access';
  sourceName: string;
  patientId: number;
  patientDisplayId: string | null;
  patientName: string;
  birthDate: string | null;
  orderedAt: string | null;
  orderNumber: string | null;
  testSummary: string | null;
  orderFound: boolean;
  status: AccessResultStatus | null;
  statusLabel: string | null;
  /** Whether the version returned (the id asked for) is the one shown on lists. */
  isCurrentVersion: boolean;
  currentVersionId: number | null;
  versions: AccessResultVersion[];
  files: LabResultFile[];
  sections: AccessResultSection[];
  notes: string[];
  summary: {
    controlId: string | null;
    providerName: string | null;
    providerNpi: string | null;
    location: string | null;
    collectedAt: string | null;
    resultedAt: string | null;
    messageAt: string | null;
    /** Set only when this version's PDF is on file and can be opened. */
    pdfReportUrl: string | null;
  };
}

/** The Access result version `id` (not necessarily the current one). */
export function getAccessLabResultDetail(id: number): Promise<AccessLabResultDetail> {
  return apiClient
    .get<ApiResponse<AccessLabResultDetail>>(`/lab-orders/detail/access/${id}`)
    .then((response) => response.data.data);
}

export function getLabMatchCandidates(
  source: LabResultSource,
  id: number,
): Promise<LabMatchCandidate[]> {
  return apiClient
    .get<ApiResponse<LabMatchCandidate[]>>(`/lab-orders/match-candidates/${source}/${id}`)
    .then((response) => response.data.data ?? []);
}

export function associateLabResult(
  source: LabResultSource,
  id: number,
  patientId: number,
): Promise<void> {
  return apiClient
    .post(`/lab-orders/associate/${source}/${id}`, { patientId })
    .then(() => undefined);
}

export interface LabResultRef {
  source: LabResultSource;
  id: number;
}

/**
 * Delete lab results that belong to no patient from the Lab Results list.
 * All or nothing: the server rejects the whole request if any result is
 * already matched. Resolves to the number of results removed — a Quest result
 * on an order removes every result on that order.
 */
export function deleteUnmatchedLabResults(items: LabResultRef[]): Promise<number> {
  return apiClient
    .post<ApiResponse<{ deleted: number }>>('/lab-orders/delete-unmatched', { items })
    .then((response) => response.data.data?.deleted ?? 0);
}

function parseDownloadFilename(contentDisposition: string | undefined): string | null {
  if (!contentDisposition) return null;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  const plainMatch = contentDisposition.match(/filename=([^;]+)/i);
  return plainMatch?.[1]?.trim() ?? null;
}

export async function downloadLabResultFile(url: string): Promise<LabDownloadResult | null> {
  // Backend embeds fully path-prefixed (/api/v5/...) download URLs in the
  // detail payload, but apiClient also prepends its baseURL. In production the
  // baseURL is an ABSOLUTE url (e.g. https://api.prospera.ai/api/v5), so we
  // must compare against its PATH portion — not the whole baseURL — to strip
  // the prefix. This keeps the /api/v5 prefix applied exactly once and avoids
  // the doubled /api/v5/api/v5/... 404.
  const rawBase = String(apiClient.defaults.baseURL ?? '');
  let basePath = rawBase;
  try {
    basePath = new URL(rawBase).pathname;
  } catch {
    // Relative baseURL (e.g. "/api/v5"): use as-is.
  }
  basePath = basePath.replace(/\/$/, '');
  const path = basePath && url.startsWith(basePath) ? url.slice(basePath.length) : url;
  const response = await apiClient.get<Blob>(path, { responseType: 'blob' });
  const blob = response.data;

  if (blob.type === 'application/json') {
    const json = JSON.parse(await blob.text()) as { redirect?: string };
    if (json.redirect) {
      const a = document.createElement('a');
      a.href = json.redirect;
      a.download = '';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return null;
    }
  }

  return {
    blob,
    filename: parseDownloadFilename(response.headers['content-disposition']),
    contentType: response.headers['content-type'] ?? null,
  };
}
